import { Component, computed, inject, input, output } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ContractTypes } from '@src/app/core/enum/contract-types.enum';
import { Contract } from '@src/app/core/types/contract.type';
import { ImportsModule } from '@src/app/imports';
import { isProfilingEnabled } from '@src/app/core/utils/feature-flags.util';
import { EmptyMessageComponent } from '@src/app/shared/components/empty-message/empty-message.component';
import { AuthSelectors } from '@src/app/store/auth/auth.selectors';
import { ALL_CATALOGUED_TYPES, COMMERCIAL_TYPES, DOMESTIC_TYPES, POSEEDOR_FINAL_TYPES } from './amd-contracts.constants';
import { ContractCardComponent } from './components/contract-card/contract-card.component';

@Component({
  selector: 'app-amd-contracts',
  imports: [
    TranslateModule,
    ContractCardComponent,
    ImportsModule,
    EmptyMessageComponent,
  ],
  templateUrl: './amd-contracts.component.html',
  styleUrl: './amd-contracts.component.scss',
})
export class AmdContractsComponent {
  private store = inject(Store);

  visible = input<boolean>(false);
  closeDialog = output<void>();

  loadingContracts = this.store.selectSignal(
    AuthSelectors.selectLoadingContracts,
  );

  private isPoseedorFinal = this.store.selectSignal(
    AuthSelectors.selectIsPoseedorFinal,
  );

  contracts = computed(() => {
    const raw = this.store.selectSignal(AuthSelectors.selectActiveContracts)();
    // La card de Poseedor Final solo es visible si:
    // 1. La cookie VIEW_PF está activa (perfilado PF habilitado), Y
    // 2. La empresa seleccionada tiene perfil PF (integral o coste estándar)
    const showPFContracts = isProfilingEnabled() && this.isPoseedorFinal();
    const filtered = showPFContracts
      ? raw
      : raw.filter(
          (c) => !POSEEDOR_FINAL_TYPES.includes(c.TIPO_ID as ContractTypes),
        );
    return this.processContracts(filtered);
  });

  private collapsePFCEContracts(contracts: Contract[]): Contract[] {
    const pfce = contracts.filter(
      (c) => c.TIPO_ID === ContractTypes.POSEEDOR_FINAL_COSTE_ESTANDAR,
    );
    if (pfce.length <= 1) return contracts;
    const representative = [...pfce].sort((a, b) => {
      const da = a.FCIERT_DT_PARSED?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const db = b.FCIERT_DT_PARSED?.getTime() ?? Number.MAX_SAFE_INTEGER;
      return da - db;
    })[0];
    return [
      ...contracts.filter((c) => c.TIPO_ID !== ContractTypes.POSEEDOR_FINAL_COSTE_ESTANDAR),
      representative,
    ];
  }

  private processContracts(contracts: Contract[]): Contract[] {
    const contractsWithCertificates = this.collapsePFCEContracts(contracts)
      .filter((contract) =>
        ALL_CATALOGUED_TYPES.includes(contract.TIPO_ID as ContractTypes),
      )
      .map((contract) => ({
        ...contract,
        showCertificateButton:
          DOMESTIC_TYPES.includes(contract.TIPO_ID as ContractTypes) ||
          COMMERCIAL_TYPES.includes(contract.TIPO_ID as ContractTypes),
        showDeactivateButton: !POSEEDOR_FINAL_TYPES.includes(
          contract.TIPO_ID as ContractTypes,
        ),
        literal: this.parseContractLiteral(contract.TIPO_ID, contract.TIPO_DS),
      }));

    return contractsWithCertificates.sort((a, b) => {
      const isDomesticA = DOMESTIC_TYPES.includes(a.TIPO_ID as ContractTypes);
      const isDomesticB = DOMESTIC_TYPES.includes(b.TIPO_ID as ContractTypes);
      const isCommercialA = COMMERCIAL_TYPES.includes(
        a.TIPO_ID as ContractTypes,
      );
      const isCommercialB = COMMERCIAL_TYPES.includes(
        b.TIPO_ID as ContractTypes,
      );

      if (isDomesticA && !isDomesticB) return -1;
      if (!isDomesticA && isDomesticB) return 1;

      if (isCommercialA && !isCommercialB) return -1;
      if (!isCommercialA && isCommercialB) return 1;

      return (a.TIPO_DS || '').localeCompare(b.TIPO_DS || '');
    });
  }

  private parseContractLiteral(tipoId: string, tipoDs: string): string {
    const typeMap: Record<string, string> = {
      [ContractTypes.CONTRATO_ADHESION_DOMESTICO]: 'domestic',
      [ContractTypes.REPRESENTANTES_AUTORIZADO]: 'domestic',
      [ContractTypes.ENVASES_SERVICIO]: 'domestic',
      [ContractTypes.CONTRATO_SCRAP_COMERCIAL]: 'commercial',
      [ContractTypes.REPRESENTANTE_AUTORIZADO_COMERCIALES]: 'commercial',
      [ContractTypes.CIRCULAR_CHECK]: 'circularCheck',
      [ContractTypes.EUROPEAN_DESK]: 'europeanDesk',
      [ContractTypes.POSEEDOR_FINAL_GESTION_INTEGRAL]: 'poseedorFinalIntegral',
      [ContractTypes.POSEEDOR_FINAL_COSTE_ESTANDAR]: 'poseedorFinalCosteEstandar',
    };

    return typeMap[tipoId]
      ? `accountManagement.dialog.contracts.types.${typeMap[tipoId]}`
      : tipoDs;
  }
}
